import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AnnouncementService } from 'src/app/shared/services/announcementservices/announcement.service';
import Swal from 'sweetalert2';


@Component({
  selector: 'app-announcement-grid',
  templateUrl: './announcement-grid.component.html',
  styleUrl: './announcement-grid.component.css',
  standalone: true,
  imports: [FormsModule, RouterModule]
})
export class AnnouncementGridComponent {
  public showSpinner = true;

  @Input() announcements: any[] = [];
  @Output() deleteAnnouncementEvent = new EventEmitter<number>();

  // Filters
  filterStatus: 'all' | 'active' | 'expired' = 'all';   // only active & expired announcements
  mediaFilter: 'all' | 'photos' | 'videos' = 'all';     // tabs for file types

  // Slider
  sliderOpen = false;
  sliderFiles: string[] = [];
  activeFile = '';
  activeIndex = 0;

  idSendToModal: any;
  user: any;

  constructor(
    private router: Router,
    public _announcementService: AnnouncementService
  ) { }

  /** ✅ Only include announcements with status active or expired */
  get filteredAnnouncements(): any[] {
    return this.announcements.filter(
      a => a.status === 'active' || a.status === 'expired'
    );
  }

  /** ✅ Tab switcher */
  setMediaFilter(filter: 'all' | 'photos' | 'videos') {
    this.mediaFilter = filter;
  }

  /** ✅ File filtering logic for grid + slider */
  filterFiles(files: string[]): string[] {
    if (!files) return [];

    if (this.mediaFilter === 'all') return files;
    if (this.mediaFilter === 'photos') return files.filter(f => this.isImage(f));
    if (this.mediaFilter === 'videos') return files.filter(f => this.isVideo(f));

    return [];
  }

  /** ✅ Open slider with filtered files */
  openSlider(files: string[], index: number) {
    // 1️⃣ Collect ALL files from all announcements
    const allFiles: string[] = this.announcements
      .filter((a: any) => a.status === 'active' || a.status === 'expired')
      .reduce((acc: string[], a: any) => acc.concat(a.files || []), []);

    // 2️⃣ Apply current mediaFilter for thumbnails
    if (this.mediaFilter === 'all') {
      this.sliderFiles = allFiles;
    } else if (this.mediaFilter === 'photos') {
      this.sliderFiles = allFiles.filter((f: string) => this.isImage(f)); // 👈 type for 'f'
    } else if (this.mediaFilter === 'videos') {
      this.sliderFiles = allFiles.filter((f: string) => this.isVideo(f)); // 👈 type for 'f'
    }

    // 3️⃣ Find clicked file inside the "allFiles"
    const clickedFile: string = files[index];
    this.activeIndex = this.sliderFiles.indexOf(clickedFile);

    if (this.activeIndex === -1) {
      this.activeIndex = 0;
    }

    // 4️⃣ Set active
    this.activeFile = this.sliderFiles[this.activeIndex];
    this.sliderOpen = true;
  }

  closeSlider() {
    this.sliderOpen = false;
    this.sliderFiles = [];
  }

  setActive(index: number): void {
    this.activeIndex = index;
    this.activeFile = this.sliderFiles[index];
  }

  prevFile() {
    this.activeIndex = (this.activeIndex - 1 + this.sliderFiles.length) % this.sliderFiles.length;
    this.activeFile = this.sliderFiles[this.activeIndex];
  }

  nextFile() {
    this.activeIndex = (this.activeIndex + 1) % this.sliderFiles.length;
    this.activeFile = this.sliderFiles[this.activeIndex];
  }

  // Helpers
  isImage(file: string): boolean {
    return /\.(jpg|jpeg|png|gif)$/i.test(file);
  }

  isVideo(file: string): boolean {
    return /\.(mp4|mov|avi|mkv)$/i.test(file);
  }

  editAnnouncement(id: number) {
    this.router.navigate(['/add-announcement', id]);
  }

  idSendToModaldelete(id: number) {
    this.idSendToModal = id;
  }

  deleteAnnouncement(id: any) {
    this.showSpinner = true;
    if (id) {
      this._announcementService.deleteAnnouncements(id)
        .then((value) => {
          this.showSpinner = false;
          if (value?.message === 'Announcement deleted successfully') {
            Swal.fire({
              iconHtml: '<img src="../../assets/images/success-modal.svg" alt="image">',
              title: 'Announcement Deleted Successfully',
              showConfirmButton: false,
              timer: 1500,
            }).then(() => window.location.reload());
          } else {
            Swal.fire({
              iconHtml: '<img src="../../assets/images/error-modal.svg" alt="image">',
              title: value?.message || 'Something went wrong',
              showConfirmButton: false,
              timer: 1500,
            });
          }
        })
        .catch((err) => {
          this.showSpinner = false;
          Swal.fire({
            iconHtml: '<img src="../../assets/images/error-modal.svg" alt="image">',
            title: 'Failed to delete announcement',
            showConfirmButton: false,
            timer: 1500,
          });
          console.error('Delete error:', err);
        });
    }
  }
}
